import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { LoaderComponent } from 'src/app/shared/common-ui-components/loader/loader.component';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { Router } from '@angular/router';
import { MarkdownModule } from 'ngx-markdown';
import { PostHogService } from 'src/app/shared/services/post-hog.service';
import { CopilotModalComponent } from "../../components/copilot-modal/copilot-modal.component";
import { CopilotHelperServiceService } from 'src/app/shared/services/copilot-helper-service.service';
import { CopilotStorageService } from 'src/app/shared/services/copilot-storage.service';
import { ExportDataComponent } from '../../common-ui-components/export-data/export-data.component';
import { ReportVisualDataComponent } from '../report-visual-data/report-visual-data.component';

@Component({
  selector: 'report-actions',
  standalone: true,
  imports: [
    UIIconsComponent,
    CommonModule,
    FormsModule,
    LoaderComponent,
    NgSelectModule,
    MarkdownModule,
    CopilotModalComponent,
    ExportDataComponent,
],
  templateUrl: './report-actions.component.html',
  styleUrl: './report-actions.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportActionsComponent implements OnInit, OnChanges {
  private copilotHelperService = inject(CopilotHelperServiceService);
  private copilotStorageService = inject(CopilotStorageService);
  @Input() selectedAction: any;
  @Input() reportTemplate: any;
  @Input() agGridChartType: string = 'bar'; // AG Grid chart type
  @Input() selectedScope: any;
  @Input() checkedColumns: any;
  @Input() isSavedReport: boolean = false;
  @Input() reportVisualType: string = 'CHART';
  @Input() isValidSelection: boolean = false;
  @Input() selectedTableIds: string[] = [];
  @Input() savedColumnIds: string[] = [];
  @Input() getSQLFunctionForColumn!: (columnId: string) => string;
  @Input() loadingInsights: boolean = false;
  @Input() insights: any[] = [];
  @Input() reportVisualData: any[] = []; // Needed for copilot modal
  @Input() appliedFilters: any[] = [];
  @Input() conversations: any = Input();
  @Input() reportId: string = '';
  @Input() selectedDateTimeFormat: string = '';
  @Input() appliedDateTimeFormat: string = '';
  @Input() customDateFormat: string = '';
  @Input() currentReportState: any; // For accessing agGridChartModel
  @Input() reportVisualDataComponent: any;
  @Output() showReportInsights = new EventEmitter<boolean>();
  @Output() closeQuickActionModal = new EventEmitter<boolean>();
  @Output() afterSaveReport = new EventEmitter<any>();
  @Output() generateInsights = new EventEmitter<void>();
  @Output() emitConversations = new EventEmitter<{ questions: any[], answers: any[] }>();
  @Output() askQuestion = new EventEmitter<string>();
  @Output() switchVisualizationType = new EventEmitter<'TABLE' | 'CHART'>();
  public redirectTime: number = 2;
  public intervalRedirectTime: any;
  public redirecting = signal<boolean>(false);
  public loadingAction = signal(false);
  public newAssetId: any = '';
  public reportName: string = '';
  public reportDescription: string = '';
  public reportTemplateId: string = '';
  public isDescriptionEmpty: boolean = false;
  public isReportNameEmpty: boolean = false;
  public tags: string[] = [];
  public tagInput: string = '';
  private assetsApiService = inject(AssetsApiService);
  public activeTab: number = 0;
  public saveReportLoading: boolean = false;
  public saveReportSuccessful = signal(false);
  public saveReportError = signal(false);
  private router = inject(Router);
  @Input() insightsError: string | null = null;
  private postHogService = inject(PostHogService);
  public activeInsightTab: number = 0;

  // --- reportDetails cache (used by template e.g. Copilot [reportDetails]) ---
  // getReportDetails() is expensive (tableIds, columnIds, sqlFunctionMap, grid state, chart model).
  // This getter runs on every change detection, so we cache the result and only recompute when
  // inputs change (ngOnChanges sets _reportDetailsCacheValid = false) or when the user edits
  // the Save form (reportName, reportDescription, tags).
  private _cachedReportDetails: any = null;
  private _reportDetailsCacheValid = false;
  /** Snapshot of form fields at last recompute; used to detect local edits without relying on inputs */
  private _cacheReportName = '';
  private _cacheReportDescription = '';
  private _cacheTags: string[] = [];

  public get reportDetails(): any {
    // Detect if user changed name/description/tags in the Save panel (these are component state, not @Inputs)
    const localStateChanged =
      this.reportName !== this._cacheReportName ||
      this.reportDescription !== this._cacheReportDescription ||
      JSON.stringify(this.tags ?? []) !== JSON.stringify(this._cacheTags ?? []);
    if (!this._reportDetailsCacheValid || localStateChanged) {
      this._cachedReportDetails = this.getReportDetails();
      this._reportDetailsCacheValid = true;
      this._cacheReportName = this.reportName ?? '';
      this._cacheReportDescription = this.reportDescription ?? '';
      this._cacheTags = [...(this.tags ?? [])];
    }
    return this._cachedReportDetails;
  }

  ngOnInit(): void {
    this.reportName = this.reportTemplate.name;
    this.reportDescription = this.reportTemplate.description;
    this.reportTemplateId = this.reportTemplate.id;
    this.tags = this.reportTemplate.tags ?? [];

    if (this.selectedAction?.clickAction === 'INSIGHTS' && !this.insights.length) {
      this.generateInsights.emit();
    }
  }

  private startRedirectCountdown(): void {
    this.loadingAction.set(true);
    this.redirecting.set(true);
    if (this.intervalRedirectTime) {
      clearInterval(this.intervalRedirectTime);
    }
    this.intervalRedirectTime = setInterval(() => {
      if (this.redirectTime > 1) {
        this.redirectTime--;
      } else {
        this.loadingAction.set(false);
        if(this.newAssetId != ''){
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/dashboard/reports/saved/single'], {
              queryParams: { id: this.newAssetId }
            });
          });
        }
        clearInterval(this.intervalRedirectTime);
        this.redirectTime = 5;
        this.redirecting.set(false);
      }
    }, 1000);
  }

   ngOnChanges(changes: SimpleChanges): void {
    if (this.insights && this.insights.length > 0) {
      this.activeInsightTab = 0;
    }
    if (changes['selectedAction'] &&
      changes['selectedAction'].currentValue?.clickAction === 'INSIGHTS' &&
      !this.insights?.length) {
        this.generateInsights.emit();
    }
    // Only invalidate reportDetails when inputs that affect getReportDetails() change.
    // Avoid invalidating on every input (e.g. loadingInsights) to prevent CPU spike from
    // recomputing on every CD when Copilot is open.
    const reportRelevantKeys = [
      'currentReportState', 'reportTemplate', 'checkedColumns', 'selectedTableIds',
      'savedColumnIds', 'isValidSelection', 'selectedScope', 'appliedFilters',
      'conversations', 'reportId', 'agGridChartType', 'reportVisualType', 'reportVisualData'
    ];
    const reportInputChanged = reportRelevantKeys.some((key) => changes[key]);
    if (reportInputChanged) {
      this._reportDetailsCacheValid = false;
    }
   }

  public toggleInsights(show: boolean): void {
    this.showReportInsights.emit(show);
  }

  public toggleSaveReportSuccessful(): void {
    this.saveReportSuccessful.set(!this.saveReportSuccessful());
  }

  public toggleSaveReportError(): void {
    this.saveReportError.set(!this.saveReportError());
  }

  public onSaveReport(): void {
    this.postHogService.capture(`REPORT SHELL: SAVE REPORT CLICKED`);
    this.saveReportLoading = true;
    const data: any = this.getReportDetails();

    // Proceed with saving/updating using your assetsApiService
    if (this.isSavedReport) {
      data['id'] = this.reportTemplate.id;
      this.assetsApiService.updateAsset(data).subscribe({
        next: (response) => {
          this.saveReportLoading = false;
          this.saveReportSuccessful.set(true);
          this.afterSaveReport.emit(response);
        },
        error: (err) => {
          console.error('Error updating report:', err);
          this.saveReportLoading = false;
          this.saveReportError.set(true);
        },
        complete: () => {
          this.saveReportLoading = false;
        },
      });
    } else {
      this.assetsApiService.createAsset(data).subscribe({
        next: (response) => {
          this.saveReportLoading = false;
          this.saveReportSuccessful.set(true);
          this.newAssetId = response?.data?.createAsset?.id;
          this.startRedirectCountdown();
        },
        error: (err) => {
          console.error('Error creating report:', err);
          this.saveReportLoading = false;
          this.saveReportError.set(true);
        },
        complete: () => {
          this.saveReportLoading = false;
        },
      });
    }
  }

  public onSaveAsReport(): void {
    this.postHogService.capture(`REPORT SHELL: SAVE AS REPORT CLICKED`);
    this.saveReportLoading = true;
    const data: any = this.getReportDetails();
    delete data.id;

    this.assetsApiService.createAsset(data).subscribe({
      next: (response) => {
        this.saveReportSuccessful.set(true);
        this.newAssetId = response?.data?.createAsset?.id;
        this.startRedirectCountdown();
        setTimeout(() => {
          this.saveReportLoading = false;
        },5);
      },
      error: (err) => {
        console.error('Error creating report:', err);
        this.saveReportLoading = false;
        this.saveReportError.set(true);
      },
      complete: () => {
        this.saveReportLoading = false;
      },
    });
  }
  
  public changeTab(tab: number) {
    this.activeTab = tab;
  }

  public closeActionModal(): void {
    this.closeQuickActionModal.emit(true);
  }

  public addTag() {
    const tag = this.tagInput.trim();
    if (tag) {
      const tags = [...this.tags] as string[];
      if (!tags.includes(tag)) {
        tags.push(tag);
        this.tags = tags;
      }
      this.tagInput = '';
    }
  }

  public removeTag(tag: string) {
    this.tags = this.tags.filter((t) => t !== tag);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ';') {
        event.preventDefault();
        this.addTag();
    }
  }

  reGenerateInsights(): void{
    this.generateInsights.emit();
  }
  
  onConversations(payload: { questions: any[]; answers: any[] }) {
    this.emitConversations.emit(payload);

    if (this.isSavedReport && this.reportTemplate?.id) {
      this.copilotStorageService.saveConversations(this.reportTemplate.id, {
        questions: payload.questions,
        answers: payload.answers
      });
    }
  }

  onAskQuestion(question: string) {
    this.askQuestion.emit(question);
  }

  getReportDetails(): any {
    const computedTableIds = Array.from(
      new Set(
        this.checkedColumns
          .map((column: any) => column.tableId)
          .filter((id: any) => id !== null && id !== undefined)
      )
    );
    const tableIds = computedTableIds.length > 0 ? computedTableIds : this.selectedTableIds;

    const columnIds = this.isValidSelection
      ? Array.from(new Set(this.checkedColumns.map((column: any) => column.id)))
      : this.savedColumnIds;

    const sqlFunctionMap = this.checkedColumns.reduce((map: any, column: any) => {
      const sqlFunction = this.getSQLFunctionForColumn(column.id);
      if (sqlFunction) {
        map[column.id] = sqlFunction;
      }
      return map;
    }, {});

    const gridState = ReportVisualDataComponent.getCurrentGridState();

    let agGridChartModel = this.currentReportState?.agGridChartModel || null;
    if (this.reportVisualType === 'CHART' && this.reportVisualDataComponent) {
      const currentChartModel = this.reportVisualDataComponent.getChartModel();
      if (currentChartModel) {
        agGridChartModel = currentChartModel;
        console.log('Captured chart model directly before save:', agGridChartModel);
      }
    }

    return {
      ...(this.reportId ? { id: this.reportId } : {}),
      name: this.reportName,
      type: 'REPORT',
      status: 'OPEN',
      description: this.reportDescription,
      tags: this.tags,
      parameters: {
        datascopeId: this.selectedScope?.id,
        dataScopeName: this.selectedScope?.name,
        templateId: this.isSavedReport
          ? this.reportTemplate.parameters.templateId
          : this.reportTemplateId,
        tableIds,
        columnIds,
        agGridChartType: this.agGridChartType,
        agGridChartModel: agGridChartModel,
        reportVisualType: this.reportTemplate?.parameters?.reportVisualType || this.reportVisualType,
        sqlFunctionMap,
        appliedFilters: this.appliedFilters,
        gridFilterModel: gridState.filter,
        gridSortModel: gridState.sort,
        CONVERSATION_MESSAGES: this.conversations ?  this.copilotHelperService.convertQuestionAndAnswersToConversations(this.conversations ?? {}) : [],
        selectedDateTimeFormat: this.selectedDateTimeFormat,
        appliedDateTimeFormat: this.appliedDateTimeFormat,
        customDateFormat: this.customDateFormat,
        connectionId: this.selectedScope?.connectionId,
        INSIGHTS: this.insights
      },
    };
  }

  public clearInsightsError() {
    this.insightsError = null;
  }

  public onSwitchToChartView(): void {
    this.switchVisualizationType.emit('CHART');
  }
}
